import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  Dispatch,
  SetStateAction,
} from 'react'
import { useAxios } from '@/contexts/AxiosProvider'
import {
  Hall,
  TranscriptsByStep,
  TurnTranscript,
  Ballot,
  HallMetadata,
  defaultHallMetadataFE,
  DebateFormat,
} from '@db8/shared'
import { useUser } from '@clerk/clerk-react'
import { useJudgementPubSub } from '@/hooks/pub-sub/use-judgement-pub-sub'
import { useTranscriptsPubSub } from '@/hooks/pub-sub/use-transcripts-pub-sub'
import { useRouter } from '@/hooks/useRouter'
import { useHallMetadataPubSub } from '@/hooks/pub-sub/useHallMetadataPubSub'
import { useHallPubSub } from '@/hooks/pub-sub/use-hall-pub-sub'
import { useBallotsPubSub } from '@/hooks/pub-sub/use-ballots-pub-sub'

export type ArgumentsContextType = {
  judgment: string
  handleJudgment: Dispatch<SetStateAction<string | null>>
  transcriptions: TranscriptsByStep
  addTranscript: (step: number, newTranscript: TurnTranscript, noPublish?: boolean) => void
  deleteTranscript: (
    step: number,
    newTranscript: TurnTranscript,
    noPublish?: boolean,
  ) => Promise<void>
  replaceTranscript: (step: number, updated: TurnTranscript) => void
  updateTranscript: (step: number, updated: TurnTranscript) => void
}

export type HallContextType = {
  hall?: Hall | null
  updateHall?: (updatedHall: Partial<Hall>) => void
  isOwner: boolean
  hallMetadata: any
  setHallMetadata: Dispatch<SetStateAction<any>>
  setLocalMetadata: Dispatch<SetStateAction<any>>
  runTour: boolean
  setRunTour: (updatedHall: boolean) => void
  handleBallot: (ballot: Ballot[]) => void
  ballots: Ballot[]
  resetHall: (metaData) => void
  lastSender: String
  setLoaderMessages: Dispatch<SetStateAction<string[]>>
} & ArgumentsContextType

const HallContext = createContext<HallContextType>({
  judgment: '',
  handleJudgment: () => {},
  transcriptions: {},
  addTranscript: () => {},
  deleteTranscript: async () => {},
  replaceTranscript: () => {},
  updateTranscript: () => {},
  hall: null,
  isOwner: false,
  hallMetadata: {},
  setHallMetadata: () => {},
  setLocalMetadata: () => {},
  runTour: false,
  setRunTour: () => {},
  handleBallot: () => {},
  ballots: [],
  resetHall: () => {},
  lastSender: '',
  setLoaderMessages: () => {},
})

export function HallProvider({
  children,
  hall,
  setHall,
  setLoaderMessages,
}: {
  children: React.ReactNode
  hall: Hall
  setHall: Dispatch<SetStateAction<any>>
  setLoaderMessages: Dispatch<SetStateAction<string[]>>
}) {
  const user = useUser()

  const [judgment, setJudgment] = useState('')
  const [transcriptions, setTranscriptions] = useState({})
  const [ballots, setBallots] = useState([])
  const [hallMetadata, setHallMetadata] = useState<HallMetadata>(
    () => defaultHallMetadataFE(hall.format as DebateFormat) as HallMetadata,
  )
  const [isOwner, setIsOwner] = useState<boolean>(false)
  const [runTour, setRunTour] = useState(false)

  const axios = useAxios()
  const router = useRouter()

  const { publishJudgement } = useJudgementPubSub({
    judgment,
    setJudgment,
  })

  const { publishTranscripts } = useTranscriptsPubSub({
    transcriptions,
    setTranscriptions,
    hallId: hall?.id,
  })

  const { publishHallMetadata, lastSender } = useHallMetadataPubSub({
    hallMetadata,
    setHallMetadata,
    hallId: hall?.id,
  })

  const { publishBallots } = useBallotsPubSub({
    ballots,
    setBallots,
  })

  const { publishHall } = useHallPubSub({
    hall,
    setHall,
  })

  useEffect(() => {
    if (hall) {
      setBallots(hall.ballots)
      const userIsOwner = hall?.ownerId === user?.user?.id
      setIsOwner(userIsOwner)
    }
  }, [hall])

  const updateHall = async (updatedHall: Partial<Hall>) => {
    if (!hall) return

    try {
      publishHall({ ...hall, ...updatedHall })
      const newHall = await axios.put(`/api/hall/${hall.hallId}`, updatedHall)
      setHall(newHall.data)
    } catch (error) {
    }
  }

  useEffect(() => {
    if (hallMetadata?.isClosed) router.push('/')
  }, [hallMetadata])

  const updateHallMetadata = (updates: Partial<typeof hallMetadata>) => {
    //Update local state by merging
    setHallMetadata((prev) => ({ ...prev, ...updates }))
    //publish only the patch so receivers can merge cleanly
    publishHallMetadata(updates)
  }

  const addTranscript = (step: number, newTranscript: TurnTranscript, noPublish?: boolean) => {
    setTranscriptions((prev) => {
      const existing = prev[step] ?? []
      const updated = [...existing, newTranscript]
      const newTranscriptions = {
        ...prev,
        [step]: updated,
      }

      if (!noPublish) publishTranscripts(newTranscriptions)
      return newTranscriptions
    })
  }

  const deleteTranscript = async (
    step?: number,
    transcriptToDelete?: TurnTranscript | null,
    noPublish?: boolean,
  ) => {
    try {
      // single transcript by id
      if (!noPublish && transcriptToDelete) {
        await axios.delete(`/transcription/transcript/${transcriptToDelete.id}`)
      }

      // delete ALL transcripts (explicit: step is null/undefined)
      if (step == null) {
        publishTranscripts({})
        setTranscriptions({})
        await axios.delete(`/transcription/transcript/reset/${hall.id}`)
        return
      }

      // delete all transcripts for a specific step
      if (transcriptToDelete === null) {
        const { [step]: _removed, ...rest } = transcriptions as TranscriptsByStep
        publishTranscripts(rest)
        setTranscriptions(rest)
        await axios.delete(`/transcription/transcript/by-step/${hall.id}`, {
          params: { step },
        })
        return
      }

      // local remove single transcript from that step
      if (transcriptToDelete) {
        const existing = transcriptions[step] ?? []
        const updated = existing.filter((t) => t.id !== transcriptToDelete.id)
        const next = { ...transcriptions, [step]: updated }
        if (!noPublish) publishTranscripts(next)
        setTranscriptions(next)
      }
    } catch (err) {
    }
  }

  const updateTranscript = (step: number, updated: TurnTranscript) => {
    setTranscriptions((prev) => {
      const stepTranscripts = prev[step] ?? []
      const existingIndex = stepTranscripts.findIndex((t) => t.id === updated.id)

      let updatedStepTranscripts

      if (existingIndex === -1) {
        updatedStepTranscripts = [...stepTranscripts, updated]
      } else {
        updatedStepTranscripts = [...stepTranscripts]
        updatedStepTranscripts[existingIndex] = updated
      }

      const newTranscriptions = {
        ...prev,
        [step]: updatedStepTranscripts,
      }

      publishTranscripts(newTranscriptions)
      return newTranscriptions
    })
  }

  const handleBallot = (ballot) => {
    if (!Array.isArray(ballot)) return
    setBallots(ballot)
    publishBallots(ballot)
  }

  const replaceTranscript = (step: number, updated: TurnTranscript) => {
    const newTranscriptions = {
      ...transcriptions,
      [step]: [updated],
    }

    setTranscriptions(newTranscriptions)
    publishTranscripts(newTranscriptions)
  }

  const resetHall = async (defaultMetadata: HallMetadata) => {
    deleteTranscript(null, null)
    handleBallot([])

    setHallMetadata(defaultMetadata)
    publishJudgement('__RESET__') //* Strange bug with pubsub won't allow it to be '' or null.
  }
  return (
    <HallContext.Provider
      value={{
        judgment,
        handleJudgment: publishJudgement,
        transcriptions,
        addTranscript,
        deleteTranscript,
        replaceTranscript,
        updateTranscript,
        hall,
        updateHall,
        isOwner,
        hallMetadata,
        setHallMetadata: updateHallMetadata,
        setLocalMetadata: setHallMetadata,
        runTour,
        setRunTour,
        handleBallot,
        ballots,
        resetHall,
        lastSender,
        setLoaderMessages,
      }}
    >
      {children}
    </HallContext.Provider>
  )
}

export const useHall = () => {
  const context = useContext(HallContext)
  if (!context) {
    throw new Error('useHallContext must be used within a HallProvider')
  }
  return context
}
